import { BillingAddonReferenceFragment, GetDatoModulesQuery } from 'generated/resources/graphql'

import { BUSINESS_VARIANT, isOnePlan } from 'modules/upsell/lib/selfUpsell/plans/utils'

type ModulesDatoModule = NonNullable<
  GetDatoModulesQuery['modules']['datoModulesConnection']['nodes'][number]
>

export interface ButtonVisibilityParams {
  isInstalled: boolean
  variant: string
  hasEnterpriseVariant: boolean
  canSeeSelfUpsell: boolean
  canSeeNormalUpsell: boolean
  ignoreUpsell: boolean
  isLargeSegment: boolean
  hasMeetingLink?: boolean
  hasRequestedToday?: boolean
  hasRequestedTodayLoading?: boolean
  addonReference: BillingAddonReferenceFragment[]
  moduleData?: ModulesDatoModule | null
}

export interface ButtonVisibilityResult {
  showSelfUpsellButton: boolean
  shouldShowUpgradeButton: boolean
  shouldShowBookAMeetingButton: boolean
  shouldShowRequestUpsellButtonPrimary: boolean
  shouldShowRequestUpsellButtonSecondary: boolean
  showAlreadyRequestedStatus: boolean
}

const isRecruitment = (addonReference: BillingAddonReferenceFragment[]): boolean => {
  return addonReference.some((addon) => addon.internalTitle.toLowerCase().includes('recruitment'))
}

const isExpenses = (addonReference: BillingAddonReferenceFragment[]): boolean => {
  return addonReference.some((addon) => addon.internalTitle.toLowerCase().includes('expenses'))
}

const isOne = (addonReference: BillingAddonReferenceFragment[]): boolean => {
  return addonReference.some((addon) => isOnePlan(addon.internalTitle))
}

export const getButtonVisibility = (params: ButtonVisibilityParams): ButtonVisibilityResult => {
  const {
    isInstalled,
    variant,
    hasEnterpriseVariant,
    canSeeSelfUpsell,
    canSeeNormalUpsell,
    ignoreUpsell,
    isLargeSegment,
    hasMeetingLink = false,
    addonReference,
    moduleData,
  } = params

  const hasRequestedToday = params.hasRequestedToday ?? false
  const hasRequestedTodayLoading = params.hasRequestedTodayLoading ?? false

  const disableSelfUpsell = Boolean(moduleData?.disableSelfUpsell)
  const meetingOnlyFlow = disableSelfUpsell && !!canSeeNormalUpsell && !isInstalled

  const showSelfUpsellButton =
    (canSeeSelfUpsell || ignoreUpsell) && !isInstalled && !disableSelfUpsell

  const shouldShowUpgradeButton =
    (isInstalled && isOne(addonReference) && canSeeSelfUpsell && !disableSelfUpsell) ||
    (isInstalled &&
      variant === BUSINESS_VARIANT &&
      canSeeSelfUpsell &&
      hasEnterpriseVariant &&
      !isRecruitment(addonReference) &&
      !isExpenses(addonReference))

  const shouldShowBookAMeetingButton =
    !hasRequestedTodayLoading &&
    ((hasMeetingLink && !isInstalled) || (meetingOnlyFlow && hasMeetingLink))

  const shouldShowRequestUpsellButtonPrimary =
    !hasRequestedTodayLoading &&
    ((canSeeNormalUpsell &&
      !isInstalled &&
      !disableSelfUpsell &&
      !showSelfUpsellButton &&
      !shouldShowUpgradeButton) ||
      meetingOnlyFlow)

  const shouldShowRequestUpsellButtonSecondary =
    !hasRequestedTodayLoading &&
    !shouldShowRequestUpsellButtonPrimary &&
    ((canSeeNormalUpsell && !isInstalled && isLargeSegment && !disableSelfUpsell) ||
      meetingOnlyFlow)

  const showAlreadyRequestedStatus = hasRequestedToday && !isInstalled

  // TEMPORARY: Always show "request a meeting" instead of "book a meeting"
  return {
    showSelfUpsellButton,
    shouldShowUpgradeButton,
    shouldShowBookAMeetingButton: false,
    shouldShowRequestUpsellButtonPrimary,
    shouldShowRequestUpsellButtonSecondary:
      !shouldShowRequestUpsellButtonPrimary &&
      (shouldShowRequestUpsellButtonSecondary || shouldShowBookAMeetingButton),
    showAlreadyRequestedStatus,
  }
}
